import React, { useState } from 'react';
import { collection, doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ROLE_LABEL } from '../roles';
import { Wrench, Check, X as XIcon, AlertTriangle, Loader } from 'lucide-react';

// Every platform-wide maintenance task, in one place.
//
// It used to be a tab inside Staff Account, which meant the platform owner had to pick a school and
// open a school module in order to run something that walks EVERY school. The scope of the panel and
// the scope of the screen it lived on did not match. It belongs in the developer portal.
//
// Extracted rather than duplicated: there is one implementation of the silence timeout and the step
// runner, and neither depends on a school being selected.
const MigrationPanel = ({ isMobile = false, onComplete }) => {
  // These live with the panel, not with whatever screen hosts it. Leaving them behind in Staff
  // Account is what made the extracted component render and immediately throw -- and the build was
  // green, because Vite does not check that an identifier in JSX exists.
  const [migrationBusy, setMigrationBusy] = useState(false);
  const [migrationLog, setMigrationLog] = useState([]);
  // Queues a maintenance task and waits for the Cloud Function to write a result back onto the same
  // document. Times out rather than hanging forever, because the commonest reason for silence is
  // that the functions have not been deployed yet -- which is worth saying out loud.
  // Waits for the Cloud Function to write a result back onto the same document.
  //
  // The timeout is a SILENCE timeout, not a total-runtime one: these tasks walk every school and can
  // legitimately run for minutes, and the previous fixed 3-minute limit reported a healthy run as
  // dead. Every progress update from the function resets the clock, so only genuine silence fails.
  const runMaintenance = (task, onProgress) => new Promise((resolve, reject) => {
    const SILENCE_MS = 120000;
    let unsub = null;
    let timer = null;

    const fail = (msg) => {
      if (unsub) unsub();
      reject(new Error(msg));
    };

    const armTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fail(
        'No response for 2 minutes. Check that the Cloud Functions are deployed '
        + '(firebase deploy --only functions) and look at the function logs — a task killed by the '
        + 'platform never gets to write its result, so silence usually means it crashed or timed out.'
      ), SILENCE_MS);
    };

    (async () => {
      try {
        const ref = doc(collection(db, '_maintenance'));
        await setDoc(ref, { task, status: 'PENDING', createdAt: serverTimestamp() });
        armTimer();

        unsub = onSnapshot(ref, (snap) => {
          const d = snap.data();
          if (!d) return;
          armTimer(); // any write from the function means it is still alive
          if (d.status === 'PENDING') {
            if (d.progress && onProgress) onProgress(d.progress);
            return;
          }
          clearTimeout(timer);
          unsub();
          if (d.status === 'SUCCESS') resolve(d.message || 'Done.');
          else reject(new Error(d.message || 'The task reported an error.'));
        }, (e) => {
          if (timer) clearTimeout(timer);
          reject(e);
        });
      } catch (e) {
        if (timer) clearTimeout(timer);
        reject(e);
      }
    })();
  });

  const runMigrationSteps = async (steps) => {
    setMigrationBusy(true);
    setMigrationLog(steps.map(st => ({ ...st, status: 'pending', message: '' })));

    for (let i = 0; i < steps.length; i += 1) {
      setMigrationLog(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'running' } : r));
      try {
        const message = await runMaintenance(steps[i].task, (text) =>
          setMigrationLog(prev => prev.map((r, idx) => idx === i ? { ...r, message: text } : r)));
        setMigrationLog(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'done', message } : r));
      } catch (e) {
        setMigrationLog(prev => prev.map((r, idx) => (
          idx === i ? { ...r, status: 'failed', message: e.message } :
          idx > i ? { ...r, status: 'skipped', message: 'Not run -- the previous step failed.' } : r
        )));
        setMigrationBusy(false);
        return;
      }
    }

    setMigrationBusy(false);
    // Optional: the account list only exists in Staff Account, and these tasks walk every school on
    // the platform, so there is nothing here for the developer portal to refresh.
    if (onComplete) onComplete();
  };

  return (

      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '1rem' : '1.75rem' }}>
        <div style={{ maxWidth: '760px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          <div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>Migrate to the new role structure</h2>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', margin: '0.4rem 0 0', lineHeight: 1.6 }}>
              Roles used to be stored inconsistently &mdash; <code>admin</code> in lower case,
              <code>Teacher</code> and <code>Staff</code> capitalised, and whatever job title a
              school typed into a CSV import. This rewrites every account to the seven canonical
              roles, then restamps the login tokens to match.
            </p>
          </div>

          <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '1rem 1.25rem', backgroundColor: 'var(--color-bg-app)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: '0.6rem' }}>
              The seven roles
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {Object.entries(ROLE_LABEL).map(([id, label]) => (
                <span key={id} style={{
                  fontSize: '0.78rem', padding: '0.2rem 0.55rem', borderRadius: 'var(--radius-sm)',
                  backgroundColor: id === 'denied' ? 'var(--color-danger-light)' : 'var(--color-primary-light)',
                  color: id === 'denied' ? 'var(--color-danger-text)' : 'var(--color-primary)',
                  fontWeight: '600',
                }}>
                  {label}
                </span>
              ))}
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.75rem 0 0', lineHeight: 1.55 }}>
              Anything that cannot be resolved to one of these becomes <strong>No access</strong>.
              Those accounts can still sign in, but reach nothing until you give them a role &mdash;
              which is the point, and why you should run the check first.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'auditRoles', label: 'Check what would change' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              {migrationBusy ? 'Working...' : 'Check first (changes nothing)'}
            </button>

            <button
              className="btn-primary"
              disabled={migrationBusy}
              onClick={() => {
                if (!window.confirm(
                  'This rewrites the role on every account in every school, then restamps login '
                  + 'tokens.\n\nAccounts whose role cannot be resolved will be set to "No access" '
                  + 'and will not be able to use the portal until you assign them one.\n\nRun the '
                  + 'check first if you have not.\n\nProceed?'
                )) return;
                runMigrationSteps([
                  { task: 'auditRoles', label: 'Check what will change' },
                  { task: 'migrateRoles', label: 'Rewrite stored roles' },
                  { task: 'backfillClaims', label: 'Restamp login tokens' },
                  { task: 'backfillRecordIdentity', label: 'Stamp records with who they are about' },
                  { task: 'migrateExceptionLists', label: 'Convert access exception lists to email' },
                ]);
              }}
              style={{ width: 'auto', padding: '0.55rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <Wrench size={16} /> Run full migration
            </button>
          </div>

          {migrationLog.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {migrationLog.map((row, i) => {
                const tone =
                  row.status === 'done' ? 'var(--color-success-text)' :
                  row.status === 'failed' ? 'var(--color-danger)' :
                  row.status === 'running' ? 'var(--color-primary)' :
                  'var(--color-text-muted)';
                return (
                  <div key={i} style={{
                    border: '1px solid var(--color-border)',
                    borderLeft: `3px solid ${tone}`,
                    borderRadius: 'var(--radius-md)',
                    padding: '0.75rem 1rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: tone, fontWeight: '650', fontSize: '0.875rem' }}>
                      {row.status === 'done' && <Check size={15} />}
                      {row.status === 'failed' && <XIcon size={15} />}
                      {row.status === 'running' && <Loader size={15} className="animate-spin" />}
                      {row.status === 'skipped' && <AlertTriangle size={15} />}
                      {row.label}
                      {row.status === 'running' && <span style={{ fontWeight: '400' }}>&mdash; this can take a minute</span>}
                    </div>
                    {row.message && (
                      <pre style={{
                        margin: '0.5rem 0 0', fontSize: '0.78rem', lineHeight: 1.5,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        color: 'var(--color-text-muted)', fontFamily: 'inherit',
                      }}>{row.message}</pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.6 }}>
            Safe to run more than once &mdash; every step only touches what is not already
            correct. Staff pick up their new token on next sign-in, or within the hour.
          </p>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Access exception lists</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Per-person exceptions in Access Control used to be stored as an initial or a
                student ID. Those are labels, not identities &mdash; only an address can be
                checked against who is signed in. This converts them. Anything that cannot be
                matched to a person is removed and listed, since it grants nobody anything.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'migrateExceptionLists', label: 'Convert access exception lists to email' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Migrate exception lists
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Public school lookup</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                The login screen has to show a school&rsquo;s name and badge before anybody signs
                in. It used to get them by querying every school by domain, which meant the whole
                school list was public &mdash; a directory of every customer. It now reads one
                document per subdomain in <code>tenant_lookup</code>, which a Cloud Function keeps
                in step. This creates that document for schools that existed before the function
                did. Run it <strong>before</strong> deploying the rules: until every school has an
                entry, its login screen shows no branding. Safe to re-run.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'backfillTenantLookup', label: 'Publish the school lookup' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Publish the school lookup
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Per-student rows</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                A notice, a payment and an activity each used to carry the whole recipient list on
                one document &mdash; so a family who could read theirs could read everyone's. Each
                now gets one row per student. This creates those rows for anything issued before
                the change. Safe to re-run.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'fanOutRecipients', label: 'Create per-student rows' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Create per-student rows
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Module admins</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Notice &amp; Payment and Salary Audit each kept their own list of authorised
                staff. This copies both into the single Module Admins list. Nothing is deleted
                and no behaviour changes &mdash; check the result in Access Control first, then
                the rules get switched over separately.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'migrateModuleAdmins', label: 'Copy existing authorisation lists' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Copy authorisation lists
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Record authors</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                New records now store who entered them. This works out the author of older ones
                from the initial, account id or address they were saved with. Anything it
                cannot resolve is reported and left alone &mdash; a wrong author would hand
                somebody else&rsquo;s record to the wrong person. Safe to re-run.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'backfillRecordOwner', label: 'Work out the author of older records' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Backfill record authors
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Duty module permissions</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Attendance, Invigilation, Patrol Duty and Quiz &amp; Tutorial each kept their own
                list of individually-named editors. This promotes those people to module admins,
                and writes Attendance&rsquo;s role defaults into each school so the security
                rules can read the same settings the screen shows. <strong>Run this before
                deploying the rules</strong> &mdash; afterwards, verification depends on it.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'migrateDutyPermissions', label: 'Fold duty permissions into module admins' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Fold duty permissions
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong>Attendance: one record per person per half-day</strong>
              <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: 1.7, maxWidth: '70ch' }}>
                Attendance rows used to carry generated ids, so nothing stopped one person having
                five rows for one morning. The id becomes{' '}
                <code>{'{date}__{half}__{person}'}</code>, which is what lets the security rules
                refuse a second write rather than relying on the screen to guard it.{' '}
                <strong>This one deletes rows</strong> &mdash; where two rows want the same slot the
                earliest is kept, which is the same rule the registers already use. Check first.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                className="btn-outline"
                disabled={migrationBusy}
                onClick={() => runMigrationSteps([
                  { task: 'auditAttendanceIds', label: 'Check what would change' },
                ])}
                style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
              >
                Check attendance ids
              </button>
              <button
                className="btn-outline"
                disabled={migrationBusy}
                onClick={() => runMigrationSteps([
                  { task: 'migrateAttendanceIds', label: 'Rewrite attendance ids' },
                ])}
                style={{ width: 'auto', padding: '0.55rem 1.1rem', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
              >
                Rewrite attendance ids
              </button>
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Remove the old editor lists</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Deletes the per-action staff lists the four duty modules used to keep. Nothing
                reads them any more &mdash; this removes stale data that still looks like a
                permission. Only run it once the Module Admins list has been checked.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'dropLegacyDutyLists', label: 'Remove the old editor lists' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Remove old lists
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Approval rights</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Forms &amp; Application kept its approver list as staff <em>initials</em>, which no
                security rule can verify. This resolves them to emails and promotes them to module
                admins, and fills in the author of older maintenance reports.
                <strong> Run this before deploying the rules</strong> &mdash; afterwards, approving
                depends on it.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'migrateApprovers', label: 'Promote approvers to module admins' },
                { task: 'backfillRecordOwner', label: 'Fill in maintenance report authors' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Migrate approval rights
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Unify the role field</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Account records kept the role under <code>Role</code>; everything else uses
                <code> role</code>. Same values, different spelling &mdash; which never failed
                loudly, because a query against the wrong casing returns <em>zero rows</em>
                instead of an error. That is how the billing counts read zero next to 684
                students. This writes <code>role</code> onto every account; the old field is left
                alone until you run the cleanup below.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'unifyRoleField', label: 'Write role onto every account' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Unify the role field
            </button>

            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.4rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
              Once accounts look right <strong>and sign-in is confirmed working</strong>, remove
              the old field. Skips any account that has no <code>role</code> yet rather than
              stripping its only copy.
            </p>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'dropLegacyRoleField', label: 'Remove the legacy Role field' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Remove the legacy field
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Guardian accounts</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Gives every guardian already named on a student record their own account record,
                so enrolling a child is what creates the parent account rather than an
                administrator doing it by hand. Existing records are left untouched &mdash; a
                teacher who is also a parent here keeps their staff access. Safe to re-run.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'provisionExistingGuardians', label: 'Provision existing guardians' },
                { task: 'backfillClaims', label: 'Restamp tokens' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Provision guardians
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <strong style={{ fontSize: '0.9rem' }}>Passwordless parent sign-in &mdash; console setup</strong>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.6, maxWidth: '64ch' }}>
              Three things live in the Firebase Console rather than in this app. The first two
              break sign-in outright; the third decides whether parents actually open the email.
            </p>
            <ol style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0, paddingLeft: '1.3rem', lineHeight: 1.7, maxWidth: '64ch' }}>
              <li>
                <strong>Authentication &rarr; Sign-in method &rarr; Email/Password</strong> &mdash;
                switch on <em>Email link (passwordless sign-in)</em>.
              </li>
              <li>
                <strong>Authentication &rarr; Settings &rarr; Authorized domains</strong> &mdash;
                add <em>every</em> school subdomain. Wildcards are not supported, so this is a
                step to repeat each time a school is added &mdash; and the symptom of forgetting
                is a sign-in link that never sends.
              </li>
              <li>
                <strong>Authentication &rarr; Templates &rarr; Email address sign-in</strong> &mdash;
                rewrite it. The default is English, sent from a Firebase domain with no mention of
                the school. A Hong Kong parent receiving that has every reason to treat it as
                spam. Put the school&rsquo;s name in the subject and the sender, and write the
                body in Chinese. Set the reply-to address to the school&rsquo;s own.
              </li>
            </ol>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.6, maxWidth: '64ch' }}>
              The login screen reports the first two by name if they are missing, so a
              misconfiguration does not read as a mistyped address.
            </p>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Record identities</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Conduct records are stored against a student ID, but a security rule cannot turn
                an ID into an account &mdash; so each row also has to name the student and their
                guardians. New records carry it automatically; this stamps the older ones. Run it
                again after guardian addresses change.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'backfillRecordIdentity', label: 'Stamp records with who they are about' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Stamp record identities
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Check every tenant</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Looks for the faults this platform keeps producing: migrations that never ran,
                settings a module needs and does not have, guardians named on a student record with
                no account of their own, notices that were never fanned out. Changes nothing.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                <strong>A clean result is not proof of a correct rule.</strong> This reads the data,
                not <code>firestore.rules</code> &mdash; and the sales account is an administrator,
                so it passes checks that would fail for a teacher.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'tenantHealthCheck', label: 'Check every tenant' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Run health check
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Split module access</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                <code>metadata/module_access</code> decides which modules each role sees, so students
                and guardians need to read it &mdash; but it also held the per-person exception lists,
                which are email addresses. It was therefore staff-only, and{' '}
                <strong>every switch on the family tab of Access Control did nothing to the people it
                configures</strong>. The only trace was a console line.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                This moves the lists into <code>module_access_exceptions</code> so the switches can be
                opened to members. Family-side exceptions stop applying &mdash; the list naming who
                has one stays staff-readable, so a guardian could never be told they are on it. Those
                columns now offer On and Off only.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'splitModuleAccess', label: 'Split module access' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Split module access
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Duty records: stamp addresses</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Teaching and admin duties, invigilation, patrol, absences and cover all name the
                person by <strong>initial</strong>, and a rule cannot verify an initial &mdash; the only
                identity it can prove is the address on the token. So all five of those collections
                were <code>allow write: if isSchoolStaff</code>:{' '}
                <strong>any teacher could reassign anyone else&rsquo;s duties</strong>, and no rule
                could be written to stop it while the identity stayed an initial.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                This stamps an address alongside each initial and re-keys{' '}
                <code>patrol_preference</code> by address, since there the document id <em>is</em> the
                identity. Initials are left in place &mdash; every screen still displays them.
                Initials shared by two people, or matching nobody, are reported rather than guessed at.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-warning-text)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Run this <strong>before</strong> deploying the tightened duty rules. The rules test a
                field this writes, and a rule cannot fall back to data it cannot see. Afterwards, name
                the duty coordinators in Access Control &rarr; Module Admins &mdash; Teaching Duty,
                Admin Duty and Substitution now need a named admin to write.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                Only needed once. New records are stamped by a Firestore trigger as they are written,
                whichever screen wrote them &mdash; so this does not have to be re-run every time
                somebody logs an absence or arranges cover.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'migrateDutyIdentity', label: 'Stamp duty addresses' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Stamp duty addresses
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Substitution balances: reconcile</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                <code>metadata/substitution_balances</code> is an accumulator: every screen that
                records a cover duty or an absence increments it, and nothing ever recomputes it. A
                missed increment is therefore <strong>permanent</strong> &mdash; and both CSV importers
                wrote records without touching it at all, so any school that arranges cover by
                spreadsheet is already carrying a difference.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                The records are the truth: a cover duty is worth its <code>reward</code>, an absence
                costs its <code>deduction</code>, and nothing else has ever moved the number. Check
                first &mdash; the differences are the interesting part, and this rewrites a figure
                attached to people&rsquo;s workload.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-warning-text)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                It trusts the records completely. A cover duty deleted from the register takes its
                reward with it, so read the check before running the rewrite.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                className="btn-outline"
                disabled={migrationBusy}
                onClick={() => runMigrationSteps([
                  { task: 'auditSubstitutionBalances', label: 'Check balances (changes nothing)' },
                ])}
                style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
              >
                Check first (changes nothing)
              </button>
              <button
                className="btn-outline"
                disabled={migrationBusy}
                onClick={() => {
                  if (!window.confirm(
                    `This replaces every teacher’s substitution balance with the total of the cover duties and absences on record.

Run the check first and read the differences: any record that has been deleted takes its value with it.

Continue?`
                  )) return;
                  runMigrationSteps([
                    { task: 'reconcileSubstitutionBalances', label: 'Recompute balances' },
                  ]);
                }}
                style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
              >
                Recompute substitution balances
              </button>
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>OLE: remove orphaned portfolio rows</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                <code>ole_participant</code> holds one row per activity per student, and{' '}
                <strong>that row is what a family reads</strong> &mdash; the student portfolio is
                built from this collection, not from the activity. Until this was fixed, nothing ever
                deleted one: deleting an activity left every row behind, and taking a student off one
                rewrote the rows of whoever remained and left theirs.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                So a deleted activity kept showing in every participant&rsquo;s portfolio, with
                nothing on the staff side left to find it from. This is the only way to reach those
                rows. It also clears attendance rows stranded by the same deletion, and role entries
                left on an activity for students no longer on it &mdash; which otherwise come back
                if the student is ever added again.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-warning-text)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                An activity whose participant list was never saved at all is left alone: a missing
                list is not an empty one, and treating it as empty would delete every row it has.
                Those are counted separately in the check.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                className="btn-outline"
                disabled={migrationBusy}
                onClick={() => runMigrationSteps([
                  { task: 'auditOleOrphans', label: 'Find orphaned OLE rows (changes nothing)' },
                ])}
                style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
              >
                Check first (changes nothing)
              </button>
              <button
                className="btn-outline"
                disabled={migrationBusy}
                onClick={() => {
                  if (!window.confirm(
                    `This permanently deletes every ole_participant row whose activity no longer exists, or whose student is no longer on it.

Each one is a row a student or guardian can currently see for an activity that is not theirs. Deleting it removes that activity from their portfolio, which is the point — but it cannot be undone.

Run the check first and read the list.

Continue?`,
                  )) return;
                  runMigrationSteps([
                    { task: 'cleanOleOrphans', label: 'Remove orphaned OLE rows' },
                  ]);
                }}
                style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
              >
                Remove orphaned OLE rows
              </button>
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>OLE sessions</strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                An activity used to hold one date, so a five-day activity had to be five separate
                activities &mdash; and five reply slips for one thing. This gives every existing
                activity a <code>sessionDates</code> list and one session per current date, and moves
                its attendance onto that session.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0', lineHeight: 1.6, maxWidth: '60ch' }}>
                <strong>Nothing is removed.</strong> The existing attendance fields stay where they
                are, so the current screens keep working until they are rebuilt. Safe to re-run.
              </p>
            </div>
            <button
              className="btn-outline"
              disabled={migrationBusy}
              onClick={() => runMigrationSteps([
                { task: 'migrateOleSessions', label: 'Create one session per activity' },
              ])}
              style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
            >
              Create OLE sessions
            </button>
          </div>
        </div>
      </div>
  );
};

export default MigrationPanel;
